import {
  Alert,
  AlertDescription,
  AlertIcon,
  Center,
  Spinner,
} from "@chakra-ui/react"
import { Consumer, Subscription } from "@rails/actioncable"
import { useQueryClient } from "@tanstack/react-query"
import { AppContext } from "Shared/AppContext"
import { reportErrorToSentry } from "Utilities/error"
import React, { PropsWithChildren, useContext, useEffect, useRef } from "react"
import { useGetCurrentAccount } from "~/api/generated/usabilityhub-components"
import { useUsabilityhubContext } from "~/api/generated/usabilityhub-context"
import {
  CurrentAccount,
  CurrentUser,
} from "~/api/generated/usabilityhubSchemas"
import { CurrentAccountContext } from "./CurrentAccountContext"

type Props = {
  unauthenticatedFallback?: React.ReactNode
}

// Lots of pages expect information about the current account to be available immediately
// because it used to be serialized into a script tag on the page and loaded into Redux.
// For that reason this provider blocks rendering until the query finishes.
export const CurrentAccountProvider: React.FC<PropsWithChildren<Props>> = ({
  children,
  unauthenticatedFallback,
}) => {
  const { data, isLoading, isError, error } = useGetCurrentAccount(
    {},
    {
      staleTime: 60_000,
      cacheTime: 60_000,
      // Don't retry 401 errors since that just means you are not logged in
      retry: (count, error) => count < 3 && error?.status !== 401,
    }
  )
  useAccountChannel(data?.current_account?.id)

  if (isLoading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" />
      </Center>
    )
  }

  if (isError && error.status === 401 && unauthenticatedFallback) {
    return unauthenticatedFallback
  }

  if (isError) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>
          There was an error loading your account information. Please refresh
          the page and try again.
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <CurrentAccountContext.Provider value={data}>
      {children}
    </CurrentAccountContext.Provider>
  )
}

type AccountMessage = {
  action:
    | "credits.updated"
    | "payment_method.updated"
    | "subscription.updated"
    | "moderated_study_chat_message.created"
}

const useAccountChannel = (currentAccountId?: number) => {
  const { consumer } = useContext(AppContext)
  const channel = useRef<Subscription<Consumer>>()
  const queryClient = useQueryClient()
  const { queryKeyFn } = useUsabilityhubContext()

  const currentAccountQueryKey = queryKeyFn({
    path: "/api/account/current",
    operationId: "getCurrentAccount",
    variables: {},
  })
  const currentAccountTotalCreditsQueryKey = queryKeyFn({
    path: "/api/account/total_credits",
    operationId: "getAccountTotalCredits",
    variables: {},
  })
  const unreadChatsQueryKey = queryKeyFn({
    path: "/api/moderated_study_chat_messages/unread_chats",
    operationId: "listUnreadModeratedStudyChats",
    variables: {},
  })

  useEffect(() => {
    if (!currentAccountId) return

    channel.current = consumer?.subscriptions.create(
      {
        channel: "AccountChannel",
        account_id: currentAccountId,
      },
      {
        received: (message: AccountMessage) => {
          switch (message.action) {
            case "credits.updated":
              queryClient.invalidateQueries(currentAccountTotalCreditsQueryKey)
              return queryClient.invalidateQueries(currentAccountQueryKey)
            case "payment_method.updated":
            case "subscription.updated":
              return queryClient.invalidateQueries(currentAccountQueryKey)
            case "moderated_study_chat_message.created":
              return queryClient.invalidateQueries(unreadChatsQueryKey)
            default:
              reportErrorToSentry(
                new Error("AccountMessage received unexpected message"),
                { extra: { message: message } }
              )
          }
        },
      }
    )
    return () => {
      channel.current?.unsubscribe()
    }
  }, [consumer, currentAccountId, queryClient])
}

export const useCurrentAccount = () => {
  const contextData = useContext(CurrentAccountContext)

  if (!contextData) {
    throw new Error(
      "useCurrentAccount must be used within a CurrentAccountProvider"
    )
  }

  return contextData.current_account
}

export const useCurrentUser = () => {
  const contextData = useContext(CurrentAccountContext)

  if (!contextData) {
    throw new Error(
      "useCurrentAccount must be used within a CurrentAccountProvider"
    )
  }

  return contextData.current_user
}

export const useCurrentPlan = () => {
  const contextData = useContext(CurrentAccountContext)

  if (!contextData) {
    throw new Error(
      "useCurrentPlan must be used within a CurrentAccountProvider"
    )
  }

  return contextData.current_plan
}

export const useActiveStripeSubscription = () => {
  const contextData = useContext(CurrentAccountContext)

  if (!contextData) {
    throw new Error(
      "useActiveStripeSubscription must be used within a CurrentAccountProvider"
    )
  }

  return contextData.active_stripe_subscription
}

// Even though this is not a "Maybe" hook, it might return null if the user is not an admin
export const useCurrentAccountAdmin = () => {
  const contextData = useContext(CurrentAccountContext)

  if (!contextData) {
    throw new Error(
      "useCurrentAccountAdmin must be used within a CurrentAccountProvider"
    )
  }

  return contextData.current_account_admin ?? null
}

// Suitable for use when we may or may not be inside the provider, like <UsabilityHubNavbar />
// Use only if you really need it!
export const useMaybeCurrentAccount = () => {
  return useContext(CurrentAccountContext)?.current_account ?? null
}

export const useMaybeCurrentAccountAdmin = () => {
  return useContext(CurrentAccountContext)?.current_account_admin ?? null
}

export const useMaybeCurrentUser = () => {
  return useContext(CurrentAccountContext)?.current_user ?? null
}

export const useMaybeCurrentPlan = () => {
  return useContext(CurrentAccountContext)?.current_plan ?? null
}

export const useMaybeActiveStripeSubscription = () => {
  return useContext(CurrentAccountContext)?.active_stripe_subscription
}

export const useMaybeManualSubscription = (state: "active" | "pending") => {
  const contextData = useContext(CurrentAccountContext)

  return contextData?.manual_subscriptions.find((s) => s.state === state)
}

export const useRefreshCurrentAccount = () => {
  const queryClient = useQueryClient()
  const { queryKeyFn } = useUsabilityhubContext()

  const queryKey = queryKeyFn({
    path: "/api/account/current",
    operationId: "getCurrentAccount",
    variables: {},
  })

  return async () => {
    queryClient.invalidateQueries(queryKey, { exact: true })
  }
}

export const StubCurrentAccountProvider: React.FC<
  PropsWithChildren & {
    currentUser: Partial<CurrentUser>
    currentAccount?: CurrentAccount
  }
> = ({ children, currentUser, currentAccount }) => {
  const data = {
    current_user: currentUser as CurrentUser,
    current_account: currentAccount ?? ({ id: 1 } as CurrentAccount),
    manual_subscriptions: [],
  }
  return (
    <CurrentAccountContext.Provider value={data}>
      {children}
    </CurrentAccountContext.Provider>
  )
}
